import { useCallback } from 'react';
import { activeFiltersSelector, activeSortingSelector } from '~/components/casino-shared/ducks/selectors';
import { liveCasinoDataSelector } from '~/components/live-casino/ducks/selectors';
import { sortComparingMap } from '~/components/casino-shared/filters/utils';
import { EnhancedGame } from '../types';
import { selectEnableLiveCasino3 } from '~/components/brand/ducks/selectors';
import { useCasinoSharedSelector } from '../ducks/types';

// returns a method to get both filtered and sorted games for both category and submenu pages
const useFilteredGames = () => {
	const activeFilters = useCasinoSharedSelector(activeFiltersSelector);
	const activeSortingOption = useCasinoSharedSelector(activeSortingSelector);
	const enableLiveCasino3 = useCasinoSharedSelector(selectEnableLiveCasino3);
	const liveCasinoData = useCasinoSharedSelector(liveCasinoDataSelector);

	const getGameDataFromLiveFeed = useCallback(
		(game: EnhancedGame) => {
			const providerLiveData = liveCasinoData?.find(
				data => data.provider === game?.provider?.code.replace('_MOBILE', '')
			);
			const pubCode = game?.publishCode?.split('_').reverse()[0];

			if (!pubCode) return;

			return providerLiveData?.gameStates?.[pubCode];
		},
		[liveCasinoData]
	);

	const orderGamesByClosedStatus = useCallback(
		(games: EnhancedGame[]) => {
			const playableGames = games.filter(game => getGameDataFromLiveFeed(game)?.status?.isOnline !== false);
			const closedGames = games.filter(game => getGameDataFromLiveFeed(game)?.status?.isOnline === false);

			return playableGames.concat(closedGames);
		},
		[getGameDataFromLiveFeed]
	);

	const getFilteredGames = useCallback(
		(games: EnhancedGame[]) => {
			if (!games?.length) {
				return [];
			}

			const filteredGames = Object.entries(activeFilters).reduce((acc, [key, value]) => {
				if (value.length) {
					return acc.filter(game => {
						if (key === 'provider') {
							return value.some(provider => provider === game.provider.code);
						}
						if (key === 'feature') {
							return value.some(feature => game?.tags?.some(tag => tag.name === feature));
						}
						if (key === 'amount') {
							const liveGameData = getGameDataFromLiveFeed(game);

							if (liveGameData) {
								const { betLimits } = liveGameData;
								if (typeof betLimits?.min === 'number') {
									// some games have 0.5 or 0.1 as minimum bet number so lifting them up to be found in search result
									const gameMinBet = betLimits.min < 1 ? 1 : betLimits.min;

									return value.some(amount => {
										// some values are like '1-50' but some are '1000' as the highest amount so splitting might not return
										// an array with two items, thus we treat it as the higher than filter
										const [filterMinBet, filterMaxBet] = amount.split('-');

										return filterMaxBet
											? gameMinBet >= +filterMinBet && +filterMaxBet >= gameMinBet
											: gameMinBet >= +filterMinBet;
									});
								}
							}

							// no livedata found means nothing to filter for this game
							return true;
						}

						return true;
					});
				}
				return acc;
			}, games);

			const sortedGames =
				activeSortingOption === 1 ? filteredGames : filteredGames?.sort(sortComparingMap[activeSortingOption]);

			return enableLiveCasino3 ? orderGamesByClosedStatus(sortedGames) : sortedGames;
		},
		[activeFilters, activeSortingOption, enableLiveCasino3, orderGamesByClosedStatus, getGameDataFromLiveFeed]
	);

	return { getFilteredGames };
};

export default useFilteredGames;
